import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { masterDataApi, type MasterOption } from '@/api/masterData.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

type RefKind = 'countries' | 'currencies' | 'timezones' | 'taxModes';

const labels: Record<RefKind, string> = {
  countries: 'Countries',
  currencies: 'Currencies',
  timezones: 'Timezones',
  taxModes: 'Tax modes',
};

export function MasterReferenceDataPage() {
  const [data, setData] = useState<{
    countries: MasterOption[];
    currencies: MasterOption[];
    timezones: MasterOption[];
    taxModes: MasterOption[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalKind, setModalKind] = useState<RefKind | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [extra, setExtra] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await masterDataApi.all());
    } catch (err) {
      showApiError(err, 'Failed to load reference data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = (kind: RefKind) => {
    setModalKind(kind);
    setCode('');
    setName('');
    setExtra('');
  };

  const save = async () => {
    if (!modalKind || !code.trim() || !name.trim()) {
      notify.error('Code and name are required');
      return;
    }
    setSaving(true);
    try {
      if (modalKind === 'countries') {
        await masterDataApi.addCountry(code.trim().toUpperCase(), name.trim());
      } else if (modalKind === 'currencies') {
        await masterDataApi.addCurrency(code.trim().toUpperCase(), name.trim(), extra.trim() || undefined);
      } else if (modalKind === 'timezones') {
        await masterDataApi.addTimezone(code.trim(), name.trim());
      } else {
        await masterDataApi.addTaxMode(code.trim().toUpperCase(), name.trim());
      }
      notify.success('Added');
      setModalKind(null);
      load();
    } catch (err) {
      showApiError(err, 'Could not add entry');
    } finally {
      setSaving(false);
    }
  };

  const renderList = (kind: RefKind, items: MasterOption[]) => (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 dark:text-white">{labels[kind]}</h3>
        <Button onClick={() => openAdd(kind)}>
          <AddIcon fontSize="small" /> Add
        </Button>
      </div>
      <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
        {items.map((o) => (
          <li key={o.code} className="flex justify-between rounded bg-slate-50 px-2 py-1 dark:bg-slate-800">
            <span className="font-medium">{o.code}</span>
            <span className="text-slate-500">
              {o.label}
              {o.extra ? ` (${o.extra})` : ''}
            </span>
          </li>
        ))}
        {items.length === 0 && <li className="text-slate-500">No entries</li>}
      </ul>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Reference data"
        description="Countries, currencies, timezones, and tax modes used in store setup dropdowns for all organizations."
        breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Reference data' }]}
      />
      {loading ? (
        <p>Loading…</p>
      ) : data ? (
        <div className="grid gap-4 md:grid-cols-2">
          {renderList('countries', data.countries)}
          {renderList('currencies', data.currencies)}
          {renderList('timezones', data.timezones)}
          {renderList('taxModes', data.taxModes)}
        </div>
      ) : null}

      <Modal
        open={!!modalKind}
        onClose={() => setModalKind(null)}
        title={modalKind ? `Add ${labels[modalKind].slice(0, -1)}` : ''}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalKind(null)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={modalKind === 'timezones' ? 'Zone ID *' : 'Code *'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={modalKind === 'timezones' ? 'Asia/Kolkata' : 'IN'}
          />
          <Input
            label="Display name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {modalKind === 'currencies' && (
            <Input
              label="Symbol (optional)"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="₹"
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
